<script lang="ts">
  import type { Cover } from "$lib/types/shared";
  import type { SeniorstromContract } from "$lib/types/contracts";
  import MetaData from "$lib/MetaData.svelte";
  import { contentBlockComponents } from "$lib/portableText/utils";
  import { PortableText } from "@portabletext/svelte";
  import Lead from "$lib/Lead.svelte";
  import Picture from "$lib/Picture.svelte";
  import KundeserviceBanner from "$lib/CustomerServiceBanner.svelte";
  import CoverBlock from "$lib/CoverPicture.svelte";
  import TypeformButton from "$lib/TypeformButton.svelte";

  interface Props {
    data: {
      cover: Readonly<Cover>;
      seniorstrom_contract: Readonly<SeniorstromContract>;
    };
  }

  let { data }: Props = $props();

  let contract = $derived(data?.seniorstrom_contract?.contract);
  let contact = $derived(data?.seniorstrom_contract?.customer_contact);
  let discountCards = $derived(data?.seniorstrom_contract?.contract_discount_cards);
</script>

<MetaData
  data={{
    title: contract.name,
    description: contract.description,
  }}
/>

<article class="text-body leading-normal">
  <CoverBlock image={data.cover.image}>
    <h1 class="my-0 text-h-hero font-title tablet:leading-tight">
      {data.cover?.title || ""}
    </h1>
  </CoverBlock>

  <div class="mx-edge-x">
    <Lead class="laptop:ml-cover-x mb-6" --lead-width="var(--w-prose)">{contract.description}</Lead>

    {#if contract.content}
      <div class="max-w-prose landscape:hyphens-manual laptop:ml-cover-x">
        <PortableText
          value={contract.content}
          components={contentBlockComponents}
          onMissingComponent={false}
        />
      </div>
    {/if}

    {#if contract.finePrint}
      <div class="max-w-prose my-3 children-my-1 text-base">
        <PortableText
          value={contract.finePrint}
          components={contentBlockComponents}
          onMissingComponent={false}
        />
      </div>
    {/if}
  </div>

  <section class="text-center my-12 mx-edge-x">
    <h2 class="landscape:text-4xl landscape:font-title mb-6">For å bestille Seniorstrøm</h2>

    <TypeformButton
      arrow
      class="button-primary-success"
      url="https://form.typeform.com/to/euUF2JJe"
      customParameters={{ produkt: "Seniorstrøm", product_id: contract.product_id || "" }}
      >Trykk her for å bestille</TypeformButton
    >
  </section>

  {#if contract.additional_content}
    <div class="mx-edge-x">
      <PortableText
        value={contract.additional_content}
        components={contentBlockComponents}
        onMissingComponent={false}
      />
    </div>
  {/if}

  <div class="grid tablet:grid-cols-2 tablet:gap-10 my-20 mx-edge-x">
    {#if contact.about}
      <div>
        <PortableText
          value={contact.about}
          components={contentBlockComponents}
          onMissingComponent={false}
        />
      </div>
    {/if}

    <div class="text-center">
      {#if contact.imgSrc}
        <Picture
          circle
          src={contact.imgSrc}
          srcset={contact.imgSrcset}
          srcsetWebp={contact.webpSrcset}
          alt={contact.image.alt}
          width={contract.image?.asset?.metadata?.dimensions?.width}
          height={contract.image?.asset?.metadata?.dimensions?.height}
        />
      {/if}
      <p class="my-1">{contact.name}, {contact.title}</p>
      <p class="my-1 flex flex-wrap gap-[0.375ch] justify-center">
        <a href="tel:{contact.phone}">{contact.phone}</a><span>/</span><a
          href="mailto:{contact.email}">{contact.email}</a
        >
      </p>
    </div>
  </div>

  {#if contract.documents && contract.documents.length > 0}
    <ul class="my-8">
      {#each contract.documents as doc (doc)}
        <li>
          <a href={doc.url} target="_blank">{doc.title ?? "Fil uten navn"}</a>
        </li>
      {/each}
    </ul>
  {/if}

  {#if discountCards}
    <!-- loop over contract.contract_discount_cards -->
    {#each discountCards as item (item)}
      <div class="responsive-wrapper blue-box mx-edge-x">
        {#if item.card_image?.src}
          <Picture
            src={item.card_image.src}
            alt={item.card_image.alt}
            srcset={item.card_image.srcset}
            srcsetWebp={item.card_image.srcsetWebp}
            class="object-cover w-full tablet:w-35% tablet:h-initial"
            --picture-aspect-ratio="16/9"
          />
        {/if}

        {#if item.content}
          <div class="px-4 my-6 flex-1">
            <PortableText
              value={item.content}
              components={contentBlockComponents}
              onMissingComponent={false}
            />
          </div>
        {/if}
      </div>
    {/each}
  {/if}

  <KundeserviceBanner
    title="Få hjelp til å bestille Seniorstrøm eller å finne produktet som passer dine behov"
    customEmail={contact.email}
    customPhone={contact.phone}
    customFeedback="Idar har fått din henvendelse og vil ta kontakt i løpet av 1-2 virkedager."
    showNameInput={false}
    showMessageInput={false}
  />
</article>

<style>
  article :global(a) {
    hyphens: manual;
  }

  .blue-box {
    background-color: var(--primary-white);
    border-radius: 1rem;
    margin-block: var(--s-8);
  }

  .blue-box :global(img) {
    border-radius: 1rem 1rem 0 0;
  }

  .responsive-wrapper {
    display: flex;
    flex-direction: column;
  }

  @media (--tablet) {
    .blue-box {
      padding-inline-end: 2vw;
    }
    .blue-box :global(img) {
      border-radius: 1rem 0 0 1rem;
    }
    .responsive-wrapper {
      flex-direction: row;
      gap: 2rem;
    }
  }
</style>
